import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Card, CardBody, NumberInput, Input } from '@heroui/react';
import { InfoIcon } from '../../../../../components/icons';
import Typography from '../../../../../components/typography/Typography';
import { Checkbox, Select, Textarea } from '../../../../../components/ui';
import { OfferFormValues, SelectOption, ForbiddenSource, AccountData } from './types';
import { AlertBlock, CustomHeroButton } from '../../../../../components';
import { useTranslations } from '../../../../../contexts/translation.context';
import clientAccService from '../../../../../api/client/clientAccService';
import clientOfferService, {
  ModelOfferPayload,
} from '../../../../../api/client/clientOfferService';
import { showCreatorSuccess, showError } from '../../../../../utils/toast';

// Interface for offer data from API
interface OfferFromApi {
  commission?: number;
  prohibited_sources?: string[] | null;
  show_service_info_for_partner?: boolean;
  custom_media_for_partner?: boolean;
  exist_chatters?: boolean;
  chat_processing?: string;
  free_trial_access?: boolean;
  description?: string | null;
  contact_for_content?: string | null;
  status?: string;
}
interface OfferTabWithGuarantorProps {
  modelId?: string;
  accountData?: AccountData;
}

// Функция для парсинга ошибок API (поддержка detail/details, string/array)
const parseApiError = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (!data) return fallback;

  // Получаем detail или details
  const detail = data.detail ?? data.details;
  if (!detail) return fallback;

  // Если это строка - возвращаем как есть
  if (typeof detail === 'string') return detail;

  // Если это массив ошибок валидации
  if (Array.isArray(detail)) {
    const messages = detail.map((err: any) => {
      if (typeof err === 'string') return err;
      // Формат Pydantic: { loc: [...], msg: string, type: string }
      if (err.msg) {
        const field = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : '';
        return field ? `${field}: ${err.msg}` : err.msg;
      }
      return JSON.stringify(err);
    });
    return messages.join('; ');
  }

  // Если это объект с message
  if (typeof detail === 'object' && detail.msg) {
    return detail.msg;
  }

  return fallback;
};

// Функция для создания схемы валидации с переводами
const createValidationSchema = (t: (key: string) => string) => Yup.object().shape({
  incomePercentage: Yup.string()
    .required(t('accountPage.offer.validation.incomePercentageRequired'))
    .test('min-commission', t('accountPage.offer.validation.incomePercentageMin'), (value) => {
      const num = Number(value);
      return !isNaN(num) && num >= 25;
    }),
  forbiddenSources: Yup.array()
    .min(1, t('accountPage.offer.validation.forbiddenSourcesMin'))
    .required(t('accountPage.offer.validation.forbiddenSourcesRequired')),
  otherSources: Yup.string(),
  showUsername: Yup.boolean(),
  individualContent: Yup.boolean(),
  professionalTeam: Yup.boolean(),
  chatProcessing: Yup.string()
    .min(1, t('accountPage.offer.validation.chatProcessingRequired'))
    .required(t('accountPage.offer.validation.chatProcessingRequired')),
  freeTrialCampaigns: Yup.string()
    .min(1, t('accountPage.offer.validation.freeTrialRequired'))
    .required(t('accountPage.offer.validation.freeTrialRequired')),
  contentContact: Yup.string()
    .required(t('accountPage.offer.validation.contentContactRequired')),
  description: Yup.string()
    .min(10, t('accountPage.offer.validation.descriptionMin'))
    .max(500, t('accountPage.offer.validation.descriptionMax'))
    .required(t('accountPage.offer.validation.descriptionRequired')),
  hideOffer: Yup.boolean()
});

// Начальные значения формы
const initialValues: OfferFormValues = {
  incomePercentage: '',
  forbiddenSources: [],
  otherSources: '',
  showUsername: false,
  individualContent: false,
  professionalTeam: false,
  chatProcessing: '',
  freeTrialCampaigns: '',
  contentContact: '',
  description: '',
  hideOffer: false,
};

// Запрещенные источники
const forbiddenSourcesList: ForbiddenSource[] = [
  { key: 'onlyfans', label: 'OnlyFans', value: 'onlyfans' },
  { key: 'reddit', label: 'Reddit', value: 'reddit' },
  { key: 'instagram', label: 'Instagram', value: 'instagram' },
  { key: 'tiktok', label: 'TikTok', value: 'tiktok' },
  { key: 'facebook', label: 'Facebook', value: 'facebook' },
  { key: 'google', label: 'Google', value: 'google' },
  { key: 'tinder', label: 'Tinder', value: 'tinder' },
  { key: 'telegram', label: 'Telegram', value: 'telegram' },
  { key: 'twitter', label: 'Twitter', value: 'twitter' },
  { key: 'youtube', label: 'Youtube', value: 'youtube' },
];

export const OfferTabWithGuarantor: React.FC<OfferTabWithGuarantorProps> = ({
  modelId,
  accountData,
}) => {
  const { t } = useTranslations();

  // Схема валидации с переводами
  const validationSchema = useMemo(() => createValidationSchema(t), [t]);

  // Опции для селектов с переводами
  const chatProcessingOptions: SelectOption[] = useMemo(
    () => [
      {
        key: 'none',
        label: t('accountPage.offer.chatOptions.none') || 'Не указано',
        value: 'none',
      },
      {
        key: '24/7',
        label: t('accountPage.offer.chatOptions.allTime') || '24/7 чаттеры',
        value: '24/7',
      },
      {
        key: 'business',
        label: t('accountPage.offer.chatOptions.custom') || 'Кастом расписание',
        value: 'business',
      },
      {
        key: 'manual',
        label: t('accountPage.offer.chatOptions.ai') || 'ИИ чаттер',
        value: 'manual',
      },
    ],
    [t]
  );

  const freeTrialOptions: SelectOption[] = useMemo(
    () => [
      {
        key: 'enabled',
        label: t('accountPage.offer.freeTrialOptions.enabled') || 'Включены',
        value: 'enabled',
      },
      {
        key: 'disabled',
        label: t('accountPage.offer.freeTrialOptions.disabled') || 'Отключены',
        value: 'disabled',
      },
      // { key: 'conditional', label: t('accountPage.offer.freeTrialOptions.conditional') || 'Условно', value: 'conditional' }
    ],
    [t]
  );

  const [initialFormValues, setInitialFormValues] = useState<OfferFormValues>(initialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [hasExistingOffer, setHasExistingOffer] = useState(false);
  const [existingStatus, setExistingStatus] = useState<string | null>(null);
  const [customSources, setCustomSources] = useState<ForbiddenSource[]>([]);
  const [otherSourcesError, setOtherSourcesError] = useState<string | null>(null);

  const ofModelId = useMemo(() => {
    const rawId = accountData?.of_user_id || (accountData as any)?.of_data?.id || modelId;
    if (rawId === undefined || rawId === null) return rawId;
    const numericId = Number(rawId);
    return Number.isNaN(numericId) ? rawId : numericId;
  }, [accountData, modelId]);

  const mapChatProcessingToForm = useCallback((value?: string) => {
    const normalized = value?.toLowerCase().replace(/\s+/g, '_');
    switch (normalized) {
      case 'all_time':
        return '24/7';
      case 'custom_work_time':
        return 'business';
      case 'ai':
        return 'manual';
      case 'not_specified':
      default:
        return 'none';
    }
  }, []);

  const mapChatProcessingToApi = useCallback(
    (value?: string): ModelOfferPayload['chat_processing'] => {
      switch (value) {
        case '24/7':
          return 'all time';
        case 'business':
          return 'custom work time';
        case 'manual':
          return 'AI';
        case 'none':
        default:
          return 'not specified';
      }
    },
    []
  );

  const normalizeOtherSources = (otherSources?: string) => {
    if (!otherSources) return [];
    const trimmed = otherSources.trim();
    return trimmed ? [trimmed] : [];
  };

  // Validate other sources input
  const validateOtherSources = useCallback(
    (value: string, currentForbiddenSources: string[]): string | null => {
      if (!value) return null;

      const source = value.trim();
      if (!source) return null;

      if (source.length > 10) {
        return t('accountPage.offer.validation.sourceMaxLength') || 'Максимум 10 символов';
      }

      const allExistingSources = [
        ...forbiddenSourcesList.map((s) => s.value.toLowerCase()),
        ...customSources.map((s) => s.value.toLowerCase()),
        ...currentForbiddenSources.map((s) => s.toLowerCase()),
      ];

      if (allExistingSources.includes(source.toLowerCase())) {
        return (
          t('accountPage.offer.validation.sourceDuplicate') ||
          `Источник "${source}" уже существует в списке`
        );
      }

      return null;
    },
    [customSources, t]
  );

  const mapOfferToFormValues = useCallback(
    (offer?: OfferFromApi): OfferFormValues => ({
      incomePercentage:
        offer?.commission !== undefined ? String(offer.commission) : initialValues.incomePercentage,
      forbiddenSources: offer?.prohibited_sources || initialValues.forbiddenSources,
      otherSources: '',
      showUsername: offer?.show_service_info_for_partner ?? initialValues.showUsername,
      individualContent: offer?.custom_media_for_partner ?? initialValues.individualContent,
      professionalTeam: offer?.exist_chatters ?? initialValues.professionalTeam,
      chatProcessing: mapChatProcessingToForm(offer?.chat_processing),
      freeTrialCampaigns: offer?.free_trial_access ? 'enabled' : 'disabled',
      contentContact: offer?.contact_for_content || initialValues.contentContact,
      description: offer?.description || '',
      hideOffer: ['hidden', 'HIDDEN'].includes((offer?.status || '').toString()),
    }),
    [mapChatProcessingToForm]
  );

  const buildPayload = useCallback(
    (values: OfferFormValues): ModelOfferPayload => {
      const prohibitedSources = [
        ...values.forbiddenSources,
        ...normalizeOtherSources(values.otherSources),
      ];

      const freeTrialAccess = (() => {
        if (values.freeTrialCampaigns === 'enabled') return true;
        if (values.freeTrialCampaigns === 'disabled') return false;
        return true;
      })();

      return {
        commission: Number(values.incomePercentage),
        prohibited_sources: prohibitedSources,
        show_service_info_for_partner: values.showUsername,
        custom_media_for_partner: values.individualContent,
        exist_chatters: values.professionalTeam,
        chat_processing: mapChatProcessingToApi(values.chatProcessing),
        free_trial_access: freeTrialAccess,
        description: values.description,
        contact_for_content: values.contentContact || null,
        status: values.hideOffer ? 'hidden' : 'active',
      };
    },
    [mapChatProcessingToApi]
  );

  const loadOffer = useCallback(
    async (showLoading = true) => {
      if (!ofModelId) {
        setIsLoading(false);
        return;
      }

      if (showLoading) {
        setIsLoading(true);
      }
      setApiError(null);

      try {
        const response = await clientAccService.getModelById(ofModelId);
        const offerFromApi: OfferFromApi | undefined = response?.data?.offer;

        if (offerFromApi) {
          // Extract custom sources that are not in the standard list
          const standardSourceValues = forbiddenSourcesList.map((s) => s.value.toLowerCase());
          const apiSources = offerFromApi.prohibited_sources || [];
          const extraSources = apiSources.filter(
            (source) => !standardSourceValues.includes(source.toLowerCase())
          );

          // Create ForbiddenSource objects for custom sources
          const customSourceObjects: ForbiddenSource[] = extraSources.map((source) => ({
            key: source.toLowerCase(),
            label: source,
            value: source,
          }));
          setCustomSources(customSourceObjects);

          setInitialFormValues(mapOfferToFormValues(offerFromApi));
          setHasExistingOffer(true);
          setExistingStatus(offerFromApi.status || null);
        } else {
          setCustomSources([]);
          setInitialFormValues(initialValues);
          setHasExistingOffer(false);
          setExistingStatus(null);
        }
      } catch (error) {
        console.error('Ошибка загрузки оффера:', error);
        setApiError(t('accountPage.offer.loadError') || 'Не удалось загрузить оффер');
      } finally {
        setIsLoading(false);
      }
    },
    [mapOfferToFormValues, ofModelId, t]
  );

  useEffect(() => {
    loadOffer();
  }, [loadOffer]);

  const handleSubmit = useCallback(
    async (values: OfferFormValues, { setSubmitting }: any) => {
      // Validate other sources before submit
      //@ts-ignore
      const otherSourcesValidationError = validateOtherSources(
        values.otherSources,
        values.forbiddenSources
      );
      if (otherSourcesValidationError) {
        setOtherSourcesError(otherSourcesValidationError);
        setSubmitting(false);
        return;
      }
      setOtherSourcesError(null);

      setRequestLoading(true);
      setApiError(null);

      try {
        if (!ofModelId) {
          throw new Error(
            t('accountPage.offer.validation.modelNotFound') ||
              'Не удалось определить OnlyFans модель для оффера'
          );
        }

        const payload = buildPayload(values);

        if (hasExistingOffer) {
          await clientOfferService.updateOffer(ofModelId, payload);
        } else {
          await clientOfferService.createOffer(ofModelId, payload);
        }

        // Refresh offer data without showing loading state (form stays visible)
        await loadOffer(false);
        showCreatorSuccess(t('accountPage.offer.saveSuccess') || 'Оффер сохранен');
      } catch (error: any) {
        console.error('Ошибка отправки формы:', error);
        const fallback = t('accountPage.offer.saveError') || 'Не удалось сохранить оффер';
        const errorMessage = parseApiError(error, fallback);
        setApiError(errorMessage);
        showError(errorMessage);
      } finally {
        setRequestLoading(false);
        setSubmitting(false);
      }
    },
    [buildPayload, hasExistingOffer, loadOffer, ofModelId, t, validateOtherSources]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Typography variant="body2" className="text-auth-muted">
          {t('common.loading') || 'Загружаем данные оффера...'}
        </Typography>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Formik
        initialValues={initialFormValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting, submitForm }) => (
          <Form className="space-y-6">
            {/* Заголовок */}
            <div>
              <Typography variant="h6">
                {t('accountPage.offer.title') || 'Настройки оффера'}
              </Typography>
            </div>

            {apiError && (
              <AlertBlock
                data={[
                  {
                    severity: 'error',
                    title: t('common.error') || 'Ошибка',
                    description: apiError,
                  },
                ]}
              />
            )}

            {/* Основные настройки */}
            <Card className="border border-gray-90 rounded-3.5 shadow-sm">
              <CardBody className="p-4">
                <div className="flex flex-col gap-4">
                  {/* Процент от дохода */}
                  <div>
                    <Typography variant="body2" className="text-auth-ink mb-2">
                      {t('accountPage.offer.incomePercentageLabel') || '% от дохода'}{' '}
                      <span className="text-partner">*</span>
                    </Typography>
                    <NumberInput
                      label=""
                      value={Number(values.incomePercentage) || 0}
                      onValueChange={(value) => setFieldValue('incomePercentage', String(value))}
                      size="sm"
                      className="max-w-[5.9375rem] !max-h-10"
                      classNames={{
                        inputWrapper: '!max-h-10',
                      }}
                      minValue={0}
                      maxValue={100}
                      isInvalid={!!errors.incomePercentage && !!touched.incomePercentage}
                    />
                    {errors.incomePercentage && touched.incomePercentage && (
                      <Typography variant="body2" className="text-[#f31260] mt-[8px]">
                        {errors.incomePercentage}
                      </Typography>
                    )}
                  </div>

                  {/* Запрещенные источники */}
                  <div>
                    <Typography variant="body2" className="text-auth-ink mb-2">
                      {t('accountPage.offer.forbiddenSourcesLabel')}
                    </Typography>
                    <div className="flex flex-wrap items-center gap-4 py-2.5">
                      {forbiddenSourcesList.map((source) => (
                        <Checkbox
                          key={source.key}
                          isSelected={values.forbiddenSources.includes(source.value)}
                          onChange={(isSelected) => {
                            const newSources = isSelected
                              ? [...values.forbiddenSources, source.value]
                              : values.forbiddenSources.filter((s) => s !== source.value);
                            setFieldValue('forbiddenSources', newSources);
                          }}
                          label={source.label}
                        />
                      ))}
                      {/* Custom sources from API */}
                      {customSources.map((source) => (
                        <Checkbox
                          key={source.key}
                          isSelected={values.forbiddenSources.includes(source.value)}
                          onChange={(isSelected) => {
                            const newSources = isSelected
                              ? [...values.forbiddenSources, source.value]
                              : values.forbiddenSources.filter((s) => s !== source.value);
                            setFieldValue('forbiddenSources', newSources);
                          }}
                          label={source.label}
                        />
                      ))}
                    </div>
                    {errors.forbiddenSources && touched.forbiddenSources && (
                      <Typography variant="body2" className="text-auth-strength-weak mt-2">
                        {errors.forbiddenSources}
                      </Typography>
                    )}
                  </div>

                  {/* Другие источники */}
                  <div>
                    <Input
                      variant="bordered"
                      labelPlacement="outside"
                      label={t('accountPage.offer.otherSourcesLabel') || 'Другие'}
                      placeholder="Reddit"
                      value={values.otherSources}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFieldValue('otherSources', newValue);
                        const error = validateOtherSources(newValue, values.forbiddenSources);
                        setOtherSourcesError(error);
                      }}
                      description={
                        t('accountPage.offer.otherSourcesDescription') ||
                        'Укажите доп. источник, не больше 10 символов'
                      }
                      isInvalid={!!otherSourcesError}
                      errorMessage={otherSourcesError}
                      fullWidth
                      className="w-full"
                      classNames={{
                        input: 'text-auth-ink',
                        innerWrapper: 'rounded-2',
                      }}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Дополнительные параметры */}
            <Card className="border border-gray-90 rounded-3.5 shadow-sm">
              <CardBody className="p-4">
                <Typography variant="h6" className="mb-6">
                  {t('accountPage.offer.additionalParams') || 'Дополнительные параметры'}
                </Typography>

                <div className="flex flex-col gap-4 max-w-[37.5rem]">
                  {/* Чекбоксы */}
                  <div className="space-y-4">
                    <Checkbox
                      isSelected={values.showUsername}
                      onChange={(isSelected) => setFieldValue('showUsername', isSelected)}
                      label={
                        t('accountPage.offer.showUsernameLabel') || 'Показывать имя пользователя'
                      }
                      description={
                        t('accountPage.offer.showUsernameDescription') ||
                        'Ваш профиль и имя будут видны маркетологам до начала работы'
                      }
                      showIcon
                      linkedTitle
                    />

                    <Checkbox
                      isSelected={values.individualContent}
                      onChange={(isSelected) => setFieldValue('individualContent', isSelected)}
                      label={
                        t('accountPage.offer.individualContentLabel') || 'Индивидуальный контент'
                      }
                      description={
                        t('accountPage.offer.individualContentDescription') ||
                        'Готова создавать персональный контент по запросу маркетологов'
                      }
                      linkedTitle
                    />

                    <Checkbox
                      isSelected={values.professionalTeam}
                      onChange={(isSelected) => setFieldValue('professionalTeam', isSelected)}
                      label={
                        t('accountPage.offer.professionalTeamLabel') || 'Профессиональная команда'
                      }
                      description={
                        t('accountPage.offer.professionalTeamDescription') ||
                        'На странице работают опытные чаттеры, обеспечивающие высокую вовлечённость.'
                      }
                      linkedTitle
                    />
                  </div>
                  {/* Обработка чатов */}
                  <div>
                    <Select
                      label={t('accountPage.offer.chatProcessingLabel') || 'Обработка чатов'}
                      placeholder={
                        t('accountPage.offer.chatProcessingPlaceholder') || 'Выберите тип обработки'
                      }
                      selectedKeys={values.chatProcessing ? [values.chatProcessing] : []}
                      onSelectionChange={(keys) =>
                        setFieldValue('chatProcessing', Array.from(keys)[0] || '')
                      }
                      options={chatProcessingOptions}
                      isInvalid={!!errors.chatProcessing && !!touched.chatProcessing}
                      fullWidth
                    />
                    {errors.chatProcessing && touched.chatProcessing && (
                      <Typography variant="body2" className="text-auth-strength-weak mt-2">
                        {errors.chatProcessing}
                      </Typography>
                    )}
                  </div>
                  {/* Кампании Free-Trial */}
                  <div>
                    <Select
                      label={t('accountPage.offer.freeTrialLabel') || 'Кампании Free-Trial'}
                      placeholder={t('accountPage.offer.freeTrialPlaceholder') || 'Выберите статус'}
                      selectedKeys={values.freeTrialCampaigns ? [values.freeTrialCampaigns] : []}
                      onSelectionChange={(keys) =>
                        setFieldValue('freeTrialCampaigns', Array.from(keys)[0] || '')
                      }
                      options={freeTrialOptions}
                      isInvalid={!!errors.freeTrialCampaigns && !!touched.freeTrialCampaigns}
                      size="md"
                      description={
                        t('accountPage.offer.freeTrialDescription') ||
                        'Маркетологи могут запускать кампании с бесплатным пробным периодом (Free-Trial) только при наличии платной подписки — эта функция недоступна для пользователей с бесплатным тарифом.'
                      }
                      fullWidth
                    />
                    {errors.freeTrialCampaigns && touched.freeTrialCampaigns && (
                      <Typography variant="body2" className="text-auth-strength-weak mt-2">
                        {errors.freeTrialCampaigns}
                      </Typography>
                    )}
                  </div>

                  {/* Контакт для контента */}
                  <div>
                    <Input
                      label={t('accountPage.offer.contentContactLabel') || 'Контакт для контента'}
                      variant="bordered"
                      labelPlacement="outside"
                      placeholder={
                        t('accountPage.offer.contentContactPlaceholder') || 'Укажите контакт'
                      }
                      value={values.contentContact}
                      onChange={(e) => setFieldValue('contentContact', e.target.value)}
                      // isRequired
                      description={
                        t('accountPage.offer.contentContactDescription') ||
                        'Контакт для наших менеджеров, куда они могут обратиться за запросом нового контента. При вашем согласии мы также можем создать общий чат с маркетологом.'
                      }
                      fullWidth
                      className="w-full"
                      classNames={{
                        input: 'text-black',
                        inputWrapper: 'rounded-2',
                      }}
                    />
                  </div>

                  {/* Описание */}
                  <div>
                    <Textarea
                      label={t('accountPage.offer.descriptionLabel') || 'Описание'}
                      value={values.description}
                      onChange={(value) => setFieldValue('description', value)}
                      // isRequired
                      minRows={4}
                      maxRows={8}
                      description={
                        t('accountPage.offer.descriptionDescription') ||
                        'Укажите короткое описание, которое увидят маркетологи'
                      }
                      fullWidth
                      className="w-full custom-required-label"
                    />
                    {errors.description && touched.description && (
                      <Typography variant="body2" className="text-auth-strength-weak mt-2">
                        {errors.description}
                      </Typography>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Информационный блок */}
            {/* <Card className="border-2 border-partner rounded-3.5 bg-gray-98">
              <CardBody className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 bg-husky border border-[#0051bc] rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                    <InfoIcon />
                  </div>
                  <div>
                    <Typography variant="body2" className="text-partner font-medium mb-2">
                      Проверка оффера после изменений
                    </Typography>
                    <Typography variant="body2" className="text-auth-muted">
                      Каждое изменение оффера проходит модерацию. До завершения проверки он будет скрыт от маркетологов.
                    </Typography>
                  </div>
                </div>
              </CardBody>
            </Card> */}
            <AlertBlock
              data={[
                {
                  severity: 'info',
                  title:
                    t('accountPage.offer.moderationAlertTitle') ||
                    'Проверка оффера после изменений',
                  description:
                    t('accountPage.offer.moderationAlertDescription') ||
                    'Каждое изменение оффера проходит модерацию. До завершения проверки он будет скрыт от маркетологов.',
                },
              ]}
            />
            {/* Дополнительные опции */}
            <div className="space-y-4">
              <Checkbox
                isSelected={values.hideOffer}
                onChange={(isSelected) => setFieldValue('hideOffer', isSelected)}
                label={t('accountPage.offer.hideOfferLabel') || 'Скрыть оффер'}
              />
              <CustomHeroButton
                title={t('accountPage.offer.saveButton') || 'Сохранить изменения'}
                variant="primary"
                className="bg-partner text-white px-4 md:px-6 py-3 rounded-3"
                isLoading={requestLoading || isSubmitting}
                disabled={requestLoading || isSubmitting || !!otherSourcesError}
                onPress={submitForm}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
